import * as Sentry from '@sentry/nextjs'

import { CHECKOUT_ROUTES, PLANS_ROUTES } from '@/lib/sentry/performanceTracing'

/**
 * Exclusive, non-overlapping durations for the steps of the paid journey, numbered and named in
 * `docs/data-flows/paid-journey-steps.md`, as `journey.stepN_<step name>` measurements in
 * milliseconds, so a dashboard can chart p75 of each and stack them.
 *
 * Sentry's own numbers overlap: TTFB sits inside LCP, and the eligibility and session calls run
 * inside the checkout navigation. Each measurement here is one step's own time:
 * - `journey.step3_arrive_on_zoe_com`: TTFB of a page load (includes the redirect that adds
 *   `lead_id`).
 * - `journey.step4_landing_page_shown`: LCP minus TTFB.
 * - `journey.step6_checkout_ready`: from the end of step 4, landing page shown (LCP on a checkout
 *   page load, the start of a navigation to checkout), until the form is usable, minus the time
 *   spent in step 7 (eligibility check) and step 8 (payment session created) in that window. Only
 *   sent when the form really became usable.
 * - `journey.step7_eligibility_check`: `POST /public/session/eligibility`.
 * - `journey.step8_payment_session_created`: `POST /public/session`.
 * - `journey.step5a_plans_ready`: on the App plans page, from the end of step 4 (or the start of a
 *   navigation to it) until the plan cards can be chosen.
 * Step 5 (cookie banner answered), step 5b (sign up), step 9 (pay on Stripe) and step 10 (purchase
 * recorded) are set by the cookie banner, sign-up and payment timing transactions, each of which
 * ends when its step ends. Their step spans copy the transaction's own attributes.
 *
 * Sentry charts custom measurements without a unit, so every step is also sent as a child span
 * with op `journey.<step name>` whose duration is the step's own time, for `p75(span.duration)` in
 * milliseconds. Step 6's span is not wall clock: it starts at the end of step 4 and lasts for the
 * time left once step 7 and step 8 are taken out.
 */
export const JOURNEY_MEASUREMENT_UNIT = 'millisecond'

const READY_OP = 'checkout.ready'
const PLANS_READY_OP = 'plans.ready'
const ELIGIBILITY_PATH = '/payment-service/public/session/eligibility'
const SESSION_PATH = '/payment-service/public/session'

// On v7 `beforeSendTransaction` receives the finished Span objects, not their JSON.
interface SpanLike {
  op?: string
  description?: string
  startTimestamp?: number
  endTimestamp?: number
  status?: string
}

// The JSON a span is sent as, for the step spans added here.
interface StepSpanJson {
  span_id: string
  trace_id: string
  parent_span_id: string
  op: string
  description: string
  start_timestamp: number
  timestamp: number
  origin: string
  data?: Record<string, unknown>
}

interface TransactionEventLike {
  transaction?: string
  start_timestamp?: number
  timestamp?: number
  contexts?: { trace?: { op?: string; span_id?: string; trace_id?: string; data?: Record<string, unknown> } }
  measurements?: Record<string, { value: number; unit?: unknown }>
  spans?: SpanLike[]
}

type Interval = [number, number]

// Never throws: this runs inside `beforeSendTransaction`, where an exception would lose the event.
const postPathname = (description: string | undefined): string | undefined => {
  if (!description?.startsWith('POST ')) return undefined
  try {
    return new URL(description.slice('POST '.length), 'https://placeholder').pathname
  } catch {
    return undefined
  }
}

const findCall = (spans: SpanLike[], path: string): Interval | undefined => {
  const span = spans.find((candidate) => candidate.op === 'http.client' && postPathname(candidate.description) === path)
  if (span?.startTimestamp === undefined || span.endTimestamp === undefined) return undefined
  return [span.startTimestamp, span.endTimestamp]
}

/** Seconds of `[from, to]` covered by the union of `intervals`. */
const coveredWithin = (intervals: Interval[], from: number, to: number): number => {
  const clipped = intervals
    .map(([start, end]): Interval => [Math.max(start, from), Math.min(end, to)])
    .filter(([start, end]) => end > start)
    .sort((a, b) => a[0] - b[0])
  let covered = 0
  let cursor = from
  for (const [start, end] of clipped) {
    const effectiveStart = Math.max(start, cursor)
    if (end > effectiveStart) covered += end - effectiveStart
    cursor = Math.max(cursor, end)
  }
  return covered
}

const toMs = (seconds: number): number => Math.round(seconds * 1000)

const randomSpanId = (): string =>
  Array.from(crypto.getRandomValues(new Uint8Array(8)), (byte) => byte.toString(16).padStart(2, '0')).join('')

const STEP_DESCRIPTIONS: Record<string, string> = {
  step3_arrive_on_zoe_com: 'Arrive on zoe.com',
  step4_landing_page_shown: 'Landing page shown',
  step5_cookie_banner_answered: 'Cookie banner answered',
  step5a_plans_ready: 'Plans ready',
  step5b_sign_up: 'Sign up',
  step6_checkout_ready: 'Checkout ready, own time',
  step7_eligibility_check: 'Eligibility check',
  step8_payment_session_created: 'Payment session created',
  step9_pay_on_stripe: 'Pay on Stripe',
  step10_purchase_recorded: 'Purchase recorded',
}

// Set with `Sentry.setMeasurement` by transactions that end when the step ends.
const STEPS_ENDING_WITH_TRANSACTION = [
  'step5_cookie_banner_answered',
  'step5b_sign_up',
  'step9_pay_on_stripe',
  'step10_purchase_recorded',
]

/** For `beforeSendTransaction`: adds the journey measurements and step spans a transaction carries. */
export const addJourneyMeasurements = (event: TransactionEventLike): void => {
  // Never throws: an exception here would lose the whole transaction, not just the measurements.
  try {
    const measurements = (event.measurements ??= {})
    const trace = event.contexts?.trace
    const addSpan = (name: string, from: number, to: number, data?: Record<string, unknown>) => {
      if (!trace?.span_id || !trace.trace_id || to < from) return
      const span: StepSpanJson = {
        span_id: randomSpanId(),
        trace_id: trace.trace_id,
        parent_span_id: trace.span_id,
        op: `journey.${name}`,
        description: STEP_DESCRIPTIONS[name] ?? name,
        start_timestamp: from,
        timestamp: to,
        origin: 'manual',
        ...(data ? { data } : {}),
      }
      ;(event.spans ??= []).push(span)
    }
    /** Records a step lasting `duration` seconds from `from`, as a measurement and a span. */
    const step = (name: string, from: number, duration: number) => {
      if (duration < 0) return
      measurements[`journey.${name}`] = { value: toMs(duration), unit: JOURNEY_MEASUREMENT_UNIT }
      addSpan(name, from, from + duration)
    }

    const end = event.timestamp
    // The step's own attributes (e.g. `purchase_product`), so the step span can be filtered on them.
    const ownData = Object.fromEntries(Object.entries(trace?.data ?? {}).filter(([key]) => !key.startsWith('sentry.')))
    for (const name of STEPS_ENDING_WITH_TRANSACTION) {
      const value = measurements[`journey.${name}`]?.value
      if (value !== undefined && end !== undefined) addSpan(name, end - value / 1000, end, ownData)
    }

    const op = event.contexts?.trace?.op
    const start = event.start_timestamp
    if ((op !== 'pageload' && op !== 'navigation') || start === undefined) return

    const ttfb = measurements.ttfb?.value
    const lcp = measurements.lcp?.value
    if (op === 'pageload' && ttfb !== undefined) {
      step('step3_arrive_on_zoe_com', start, ttfb / 1000)
      if (lcp !== undefined) step('step4_landing_page_shown', start + ttfb / 1000, (lcp - ttfb) / 1000)
    }

    const spans = event.spans ?? []
    // A transaction that ends first (timeout, page hidden) closes a ready span as `cancelled`.
    const readyAt = (readyOp: string) => spans.find((span) => span.op === readyOp && span.status === 'ok')?.endTimestamp
    // On a page load, step 3 (arrive on zoe.com) and step 4 (landing page shown) already cover the
    // time until the page was shown; a navigation starts from its own start.
    const from = start + (op === 'pageload' ? (lcp ?? ttfb ?? 0) / 1000 : 0)

    if (event.transaction && PLANS_ROUTES.has(event.transaction)) {
      const plansReadyAt = readyAt(PLANS_READY_OP)
      // Plans ready before the largest paint add no time of their own.
      if (plansReadyAt !== undefined) step('step5a_plans_ready', from, Math.max(0, plansReadyAt - from))
    }

    if (event.transaction && CHECKOUT_ROUTES.has(event.transaction)) {
      const eligibility = findCall(spans, ELIGIBILITY_PATH)
      const session = findCall(spans, SESSION_PATH)
      if (eligibility) step('step7_eligibility_check', eligibility[0], eligibility[1] - eligibility[0])
      if (session) step('step8_payment_session_created', session[0], session[1] - session[0])

      const checkoutReadyAt = readyAt(READY_OP)
      if (checkoutReadyAt !== undefined) {
        const calls = [eligibility, session].filter((call): call is Interval => call !== undefined)
        step('step6_checkout_ready', from, checkoutReadyAt - from - coveredWithin(calls, from, checkoutReadyAt))
      }
    }
  } catch {
    // Dropped: the transaction is sent without journey measurements.
  }
}

/**
 * A child span that stays open from the page mounting until it is usable. Keeping it open keeps
 * the page load or navigation transaction alive, so its end marks when the page became usable.
 * Timing must never break the page: on any Sentry failure the step is not sent.
 */
const readySpanTimer = (name: string, op: string) => {
  let span: ReturnType<typeof Sentry.startInactiveSpan> | undefined
  return {
    start: (): void => {
      try {
        span?.end()
        span = Sentry.startInactiveSpan({ name, op, onlyIfParent: true })
      } catch {
        span = undefined
      }
    },
    end: (): void => {
      try {
        span?.setStatus('ok')
        span?.end()
      } catch {
        // Dropped, as in `start`.
      }
      span = undefined
    },
  }
}

const checkoutReady = readySpanTimer('Checkout usable', READY_OP)
const plansReady = readySpanTimer('Plans usable', PLANS_READY_OP)

/** Call when the checkout page mounts; its end marks the end of step 6, checkout ready. */
export const startCheckoutReadySpan = checkoutReady.start

/** Call once the payment and shipping elements are ready. */
export const endCheckoutReadySpan = checkoutReady.end

/** Call when the App plans page mounts; its end marks the end of step 5a, plans ready. */
export const startPlansReadySpan = plansReady.start

/** Call once the plan cards can be chosen. */
export const endPlansReadySpan = plansReady.end
